import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Container, MenuItem, TextField, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { portalApi, PublicWebsiteData } from '../services/portalApi';
import { createLeadResizeMessage, isLeadEmbedMode } from '../utils/leadEmbed';
import { useTranslation } from 'react-i18next';
import { applyLanguage } from '../i18n';

const DEFAULT_FIELDS = ['name', 'email', 'phone', 'address', 'serviceInterest', 'referralSource', 'message'];

export const PublicLeadForm: React.FC = () => {
  const { t } = useTranslation();
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const slug = useMemo(() => {
    if (routeSlug) return routeSlug;
    const subdomain = window.location.hostname.split('.')[0];
    return window.location.hostname.endsWith('.pooldial.com') && !['app', 'portal', 'www'].includes(subdomain) ? subdomain : '';
  }, [routeSlug]);
  const isEmbedded = useMemo(
    () => isLeadEmbedMode(window.location.search),
    [],
  );
  const [website, setWebsite] = useState<PublicWebsiteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', serviceInterest: '', referralSource: '', message: '', website: '' });

  useEffect(() => {
    if (!slug) { setError(t('This contact form is not available.')); setLoading(false); return; }
    portalApi.getPublicWebsite(slug).then((data) => {
      applyLanguage(data.account.customerLanguage || data.account.locale);
      setWebsite(data);
    }).catch((err) => setError(err.message)).finally(() => setLoading(false));
  }, [slug, t]);

  useEffect(() => {
    if (!isEmbedded || !slug || window.parent === window) return undefined;

    let animationFrame = 0;
    const sendHeight = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const height = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
        );
        window.parent.postMessage(createLeadResizeMessage(slug, height), '*');
      });
    };

    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(sendHeight);
    observer?.observe(document.body);
    window.addEventListener('load', sendHeight);
    sendHeight();

    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer?.disconnect();
      window.removeEventListener('load', sendHeight);
    };
  }, [error, isEmbedded, loading, slug, success]);

  const fields = website?.settings.contactFormFields?.length ? website.settings.contactFormFields : DEFAULT_FIELDS;
  const show = (field: string) => fields.includes(field);
  const businessName = website?.account.businessName || t('Pool Service');
  const primaryColor = website?.settings.primaryColor || '#016ec3';

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (form.website) return;
    if (!form.name.trim() || (!form.email.trim() && !form.phone.trim())) {
      setError(t('Please provide your name and either an email address or phone number.')); return;
    }
    setSubmitting(true); setError(null);
    try {
      const leadData = Object.fromEntries(
        Object.entries(form).filter(([key, value]) => key !== 'website' && value.trim()),
      );
      if (isEmbedded) {
        leadData.sourcePage = document.referrer.slice(0, 500) || 'embedded-lead-form';
      }
      await portalApi.createPublicLead(slug, leadData);
      setSuccess(true);
    } catch (err: any) { setError(err.message || t('Unable to send your request.')); }
    finally { setSubmitting(false); }
  };

  if (loading) return <Box sx={{ minHeight: isEmbedded ? 320 : '100vh', display: 'grid', placeItems: 'center', bgcolor: isEmbedded ? 'transparent' : 'background.default' }}><CircularProgress /></Box>;
  return <Box data-lead-embed={isEmbedded ? 'true' : undefined} sx={{ minHeight: isEmbedded ? 'auto' : '100vh', bgcolor: isEmbedded ? 'transparent' : '#f7f9fc' }}>
    {!isEmbedded && <Box sx={{ bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}><Container maxWidth="sm" sx={{ py: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
      {website?.settings.logoUrl && <Box component="img" src={website.settings.logoUrl} alt={businessName} sx={{ height: 56, maxWidth: 220, objectFit: 'contain' }} />}
      <Typography variant="h5" fontWeight={700}>{businessName}</Typography>
    </Container></Box>}
    <Container maxWidth="sm" disableGutters={isEmbedded} sx={{ py: isEmbedded ? 0 : { xs: 3, md: 5 } }}><Card sx={{ borderRadius: 2, boxShadow: isEmbedded ? 'none' : '0 12px 36px rgba(15,23,42,.08)', border: isEmbedded ? 1 : 0, borderColor: 'divider' }}><CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
      {success ? <Box sx={{ textAlign: 'center', py: 4 }}><CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} /><Typography variant="h4" fontWeight={800}>{t('Request received')}</Typography><Typography color="text.secondary" sx={{ mt: 1 }}>{t('{{businessName}} will be in touch soon.', { businessName })}</Typography></Box> : <>
        <Typography variant="h4" fontWeight={800}>{website?.settings.contactFormHeadline || t('Request Pool Service')}</Typography>
        <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>{t('Tell us about your pool and how we can help.')}</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!website?.settings.contactFormEnabled ? <Alert severity="info">{t('This contact form is not currently available.')}</Alert> : <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <input aria-hidden="true" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} style={{ position: 'absolute', left: '-10000px' }} />
          {show('name') && <TextField required label={t('Name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}
          {show('email') && <TextField label={t('Email')} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />}
          {show('phone') && <TextField label={t('Phone')} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />}
          {show('address') && <TextField label={t('Service address')} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />}
          {show('serviceInterest') && <TextField select label={t('Service needed')} value={form.serviceInterest} onChange={(e) => setForm({ ...form, serviceInterest: e.target.value })}><MenuItem value="Weekly Pool Service">{t('Weekly Pool Service')}</MenuItem><MenuItem value="Pool Repair">{t('Pool Repair')}</MenuItem><MenuItem value="One-Time Cleaning">{t('One-Time Cleaning')}</MenuItem><MenuItem value="Other">{t('Other')}</MenuItem></TextField>}
          {show('referralSource') && <TextField select label={t('How did you hear about us?')} value={form.referralSource} onChange={(e) => setForm({ ...form, referralSource: e.target.value })}><MenuItem value="Google">Google</MenuItem><MenuItem value="Referral">{t('Referral')}</MenuItem><MenuItem value="Facebook / Instagram">Facebook / Instagram</MenuItem><MenuItem value="Nextdoor">Nextdoor</MenuItem><MenuItem value="Other">{t('Other')}</MenuItem></TextField>}
          {show('message') && <TextField label={t('How can we help?')} multiline minRows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />}
          <Button type="submit" variant="contained" disabled={submitting} sx={{ bgcolor: primaryColor, py: 1.25, textTransform: 'none', fontWeight: 700 }}>{submitting ? t('Sending…') : t('Request Service')}</Button>
        </Box>}
      </>}
    </CardContent></Card></Container>
  </Box>;
};
